import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { isAdminLevel, isSuperAdmin } from '../lib/utils';
import api from '../lib/api';
import { MENU_ITEMS } from '../lib/menuItems';

const Sidebar = ({ user, onLogout }) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState({});
  const [sidebarSearch, setSidebarSearch] = useState('');

  const roleIcons = {
    super_admin: '\u{1F48E}',
    admin: '\u{1F451}',
    manager: '\u{1F4CA}', 
    salesman: '\u{1F4BC}',
    inventory_staff: '\u{1F4E6}',
    colourman: '\u{1F3A8}',
    packing_department: '\u{1F4CB}',
    agent: '\u{1F91D}',
    distributor: '\u{1F69A}'
  };

  const fetchBadges = useCallback(async () => {
    try {
      const res = await api.get('/badge-counts');
      setBadgeCounts(res.data);
    } catch (err) {
      console.error('Sidebar badges fetch failed:', err);
    }
  }, []);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 5000);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  const menuItems = MENU_ITEMS;

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const hasAccess = (roles, path, permKey, allowedUserIds) => {
    // Super Admin always has access to everything
    if (isSuperAdmin(user?.role)) return true;

    // Per-user override (used for specific user IDs granted extra access)
    if (allowedUserIds && allowedUserIds.includes(user?.id)) return true;

    // Permission-key override (allows fine-grained grants without code changes)
    if (permKey && user?.permissions?.[permKey]) {
      const hiddenModules = user?.permissions?.hidden_modules || [];
      return !hiddenModules.includes(path);
    }

    // Legacy: PO access for users with can_create_po
    if (path === '/purchase-orders' && user?.permissions?.can_create_po) {
      const hiddenModules = user?.permissions?.hidden_modules || [];
      return !hiddenModules.includes(path);
    }

    // Strict role-list enforcement: user's role must be explicitly listed
    if (!roles.includes(user?.role)) return false;
    const hiddenModules = user?.permissions?.hidden_modules || [];
    return !hiddenModules.includes(path);
  };

  return (
    <div className={`sidebar-nav transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-72'} flex flex-col h-full`}>
      {/* Header */}
      <div className={`border-b border-gray-200 ${isCollapsed ? 'p-3' : 'p-6'}`}>
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div>
              <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Nunito' }}>
                Garment360
              </h1>
              <p className="text-sm text-gray-500 mt-1">ERP System</p>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-gray-500 hover:text-gray-700"
            data-testid="sidebar-toggle-button"
          >
            <svg className={`w-4 h-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
        </div>
      </div>

      {/* Search */}
      {!isCollapsed && (
        <div className="px-4 pt-3 pb-1">
          <div className="relative">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search menu..."
              value={sidebarSearch}
              onChange={e => setSidebarSearch(e.target.value)}
              className="w-full h-8 pl-8 pr-3 text-xs bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-blue-400 focus:bg-white transition-colors placeholder-gray-400"
              data-testid="sidebar-search-input"
            />
            {sidebarSearch && (
              <button onClick={() => setSidebarSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-2">
          {menuItems.filter(item => {
            if (!hasAccess(item.roles, item.path, item.permKey, item.allowedUserIds)) return false;
            if (sidebarSearch) return item.name.toLowerCase().includes(sidebarSearch.toLowerCase());
            return true;
          }).map((item) => {
            const count = badgeCounts[item.badgeKey] || badgeCounts[item.path] || 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''} group relative ${isCollapsed ? '!justify-center !px-2 !mx-1' : ''}`}
                data-testid={`sidebar-${item.name.toLowerCase().replace(/\s+/g, '-')}-link`}
              >
                <span className={`text-lg ${isCollapsed ? '' : 'mr-3'}`}>{item.icon}</span>
                {!isCollapsed && (
                  <span className="font-medium flex-1">{item.name}</span>
                )}
                {count > 0 && !isCollapsed && (
                  <span
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-red-500 text-white"
                    data-testid={`badge-count-${item.path.replace('/', '')}`}
                  >
                    {count > 99 ? '99+' : count}
                  </span>
                )}
                {count > 0 && isCollapsed && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
                )}
                {isCollapsed && (
                  <div className="absolute left-16 ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 whitespace-nowrap flex items-center gap-2">
                    {item.name}
                    {count > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold bg-red-500 text-white">{count}</span>
                    )}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold">
            {roleIcons[user?.role] || getInitials(user?.full_name || 'User')}
          </div>
          
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.full_name}
              </p>
              <p className="text-xs text-gray-500 capitalize truncate">
                {user?.role === 'super_admin' ? 'Super Admin' : user?.role?.replace('_', ' ')}
              </p>
            </div>
          )}
        </div>
        
        {!isCollapsed && (
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full text-gray-500 hover:text-red-600 hover:bg-red-50 justify-start"
            data-testid="sidebar-logout-button"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </Button>
        )}
        
        {isCollapsed && (
          <Button
            onClick={onLogout}
            variant="ghost"
            size="sm"
            className="w-full text-gray-500 hover:text-red-600 hover:bg-red-50 p-2"
            data-testid="sidebar-logout-button-collapsed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </Button>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
